import type { Composition, Layer, Project } from './schema';
import { createId } from './project';

export function layerEnd(layer: Layer): number {
  return layer.start + layer.duration;
}

export function activeLayersAt(project: Project, composition: Composition, time: number): Layer[] {
  return composition.layerIds
    .map((id) => project.layers[id])
    .filter((layer): layer is Layer => Boolean(layer && layer.visible && time >= layer.start && time < layerEnd(layer)));
}

export function sourceTimeAt(layer: Layer, timelineTime: number): number {
  const local = Math.max(0, timelineTime - layer.start);
  const source = local * layer.speed;
  return layer.reverse ? layer.inPoint + Math.max(0, layer.duration * layer.speed - source) : layer.inPoint + source;
}

export function splitLayer(layer: Layer, atTime: number): [Layer, Layer] | null {
  if (atTime <= layer.start || atTime >= layerEnd(layer)) return null;
  const firstDuration = atTime - layer.start;
  const secondDuration = layer.duration - firstDuration;
  const left: Layer = { ...layer, duration: firstDuration };
  const right: Layer = {
    ...structuredClone(layer),
    id: createId('layer'),
    name: `${layer.name} copy`,
    start: atTime,
    duration: secondDuration,
    inPoint: layer.reverse ? layer.inPoint : layer.inPoint + firstDuration * layer.speed,
    keyframes: Object.fromEntries(Object.entries(layer.keyframes).map(([property, frames]) => [property, frames.filter((frame) => frame.time >= firstDuration).map((frame) => ({ ...frame, id: createId('kf'), time: frame.time - firstDuration }))]))
  };
  left.keyframes = Object.fromEntries(Object.entries(layer.keyframes).map(([property, frames]) => [property, frames.filter((frame) => frame.time <= firstDuration)]));
  return [left, right];
}

export function trimLayer(layer: Layer, edge: 'start' | 'end', toTime: number, minDuration = 1 / 120): Layer {
  if (edge === 'start') {
    const nextStart = Math.min(layerEnd(layer) - minDuration, Math.max(0, toTime));
    const delta = nextStart - layer.start;
    return {
      ...layer,
      start: nextStart,
      duration: layer.duration - delta,
      inPoint: layer.reverse ? layer.inPoint : Math.max(0, layer.inPoint + delta * layer.speed)
    };
  }
  return { ...layer, duration: Math.max(minDuration, toTime - layer.start) };
}

export function rippleDelete(project: Project, compositionId: string, layerIds: string[]): Project {
  const next = structuredClone(project);
  const composition = next.compositions[compositionId];
  const selected = layerIds.map((id) => next.layers[id]).filter(Boolean);
  if (!selected.length) return next;
  const start = Math.min(...selected.map((layer) => layer.start));
  const end = Math.max(...selected.map(layerEnd));
  const gap = end - start;
  for (const id of layerIds) delete next.layers[id];
  composition.layerIds = composition.layerIds.filter((id) => !layerIds.includes(id));
  for (const id of composition.layerIds) {
    const layer = next.layers[id];
    if (layer.start >= end) layer.start = Math.max(0, layer.start - gap);
  }
  next.updatedAt = Date.now();
  return next;
}

export function collectSnapTargets(project: Project, composition: Composition, excludeIds: string[] = []) {
  return [
    ...composition.layerIds.flatMap((id) => {
      const layer = project.layers[id];
      return !layer || excludeIds.includes(id) ? [] : [
        { time: layer.start, type: 'clip' as const, id },
        { time: layerEnd(layer), type: 'clip' as const, id }
      ];
    }),
    ...composition.markers.map((marker) => ({ time: marker.time, type: 'marker' as const, id: marker.id }))
  ];
}
